import React, { Suspense, lazy } from 'react'; // Institutional Registry Root - Rebuild v1.0
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { BRAND } from './config/brand';
import SnowEffect from './components/SnowEffect';
import { AnimatePresence, motion } from 'framer-motion';
import API from './api/axios';

// Lazy loaded pages
const Home = lazy(() => import('./pages/Home'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const FeaturedProperties = lazy(() => import('./pages/FeaturedProperties'));
const SubscriptionPayment = lazy(() => import('./pages/SubscriptionPayment'));
const AdminSubscriptions = lazy(() => import('./pages/AdminSubscriptions'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminBankDetails = lazy(() => import('./pages/AdminBankDetails'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminSellers = lazy(() => import('./pages/AdminSellers'));
const AdminProperties = lazy(() => import('./pages/AdminProperties'));
const PropertyPreview = lazy(() => import('./pages/PropertyPreview'));
const AdminPlans = lazy(() => import('./pages/AdminPlans'));
const AdminClassifications = lazy(() => import('./pages/AdminClassifications'));
const AdminPromos = lazy(() => import('./pages/AdminPromos'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const SupportHistory = lazy(() => import('./pages/SupportHistory'));
const AdminSupportTickets = lazy(() => import('./pages/AdminSupportTickets'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const AdminInquiries = lazy(() => import('./pages/AdminInquiries'));
const AdminMessages = lazy(() => import('./pages/AdminMessages'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const UserAdminMessages = lazy(() => import('./pages/UserAdminMessages'));
const AdminBanners = lazy(() => import('./pages/AdminBanners'));
const AdminSpotlights = lazy(() => import('./pages/AdminSpotlights'));
const AdminTeam = lazy(() => import('./pages/AdminTeam'));
const AdminVipPayments = lazy(() => import('./pages/AdminVipPayments'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const LoginHistory = lazy(() => import('./pages/LoginHistory'));
const AdminSecurityActivity = lazy(() => import('./pages/AdminSecurityActivity'));
const AdminBranding = lazy(() => import('./pages/AdminBranding'));
const AccountDisabled = lazy(() => import('./pages/AccountDisabled'));

// Protected Route for Sellers
const SellerRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user || (user.role !== 'seller' && user.role !== 'admin' && user.role !== 'superadmin')) {
        return <Navigate to="/" />;
    }
    return children;
};

// Protected Route for Admin
const AdminRoute = () => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
        return <Navigate to="/" />;
    }
    return (
        <AdminLayout>
            <Outlet />
        </AdminLayout>
    );
};

// Protected Route for all logged in users
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) {
        return <Navigate to="/login" />;
    }
    return children;
};

// Lightweight Page Transition Wrapper
const PageTransition = ({ children }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeInOut" }}
            className="w-full"
        >
            {children}
        </motion.div>
    );
};

const AppContent = () => {
    const [showSnow, setShowSnow] = React.useState(false);

    const location = useLocation();
    const background = location.state?.background;
    const lastViewedId = React.useRef(null);

    // Track the current property ID when in modal mode
    React.useEffect(() => {
        if (background) {
            const match = location.pathname.match(/\/property\/(\d+)/);
            if (match) {
                lastViewedId.current = match[1];
            }
        }
    }, [location.pathname, background]);

    // Handle scroll restoration when closing modal
    React.useEffect(() => {
        if (!background && lastViewedId.current) {
            const propertyId = lastViewedId.current;
            
            // Short delay to allow modal exit animation to finish or at least start
            const timer = setTimeout(() => {
                const element = document.getElementById(`property-${propertyId}`);
                if (element) {
                    console.log(`Restoring scroll to property-${propertyId}`);
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Add highlight effect
                    element.classList.add('ring-2', 'ring-primary/30', 'transition-all', 'duration-500');
                    setTimeout(() => {
                        element.classList.remove('ring-2', 'ring-primary/30');
                    }, 2000);
                } else {
                    console.warn(`Element property-${propertyId} not found for restoration`);
                }
                lastViewedId.current = null; // Reset
            }, 150);

            return () => clearTimeout(timer);
        }
    }, [background]);

    React.useEffect(() => {
        const controller = new AbortController();
        
        const fetchSettings = async () => {
            try {
                const res = await API.get('/settings/homepage_snow_effect', {
                    signal: controller.signal
                });
                console.log("Snow setting fetched:", res.data.value);
                setShowSnow(res.data.value === 'true');
            } catch (err) {
                if (err.name !== 'CanceledError') {
                    console.error('Error fetching global snow setting:', err);
                }
            }
        };

        fetchSettings();

        return () => controller.abort(); // Cleanup on unmount or re-run
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col relative">

            {showSnow && <SnowEffect />}
            <Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}>
                <Routes location={background || location}>
                    {/* Admin Routes with Dedicated Layout */}
                    <Route path="/admin" element={<AdminRoute />}>
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="payments" element={<AdminSubscriptions />} />
                        <Route path="vip-payments" element={<AdminVipPayments />} />
                        <Route path="bank-details" element={<AdminBankDetails />} />

                        <Route path="users" element={<AdminUsers />} />
                        <Route path="sellers" element={<AdminSellers />} />
                        <Route path="properties" element={<AdminProperties />} />
                        <Route path="properties/:id/preview" element={<PropertyPreview />} />
                        <Route path="plans" element={<AdminPlans />} />
                        <Route path="classifications" element={<AdminClassifications />} />
                        <Route path="messages" element={<AdminMessages />} />
                        <Route path="inquiries" element={<AdminInquiries />} />
                        <Route path="promo-codes" element={<AdminPromos />} />
                        <Route path="support-tickets" element={<AdminSupportTickets />} />
                        <Route path="banners" element={<AdminBanners />} />
                        <Route path="spotlights" element={<AdminSpotlights />} />
                        <Route path="team" element={<AdminTeam />} />
                        <Route path="security-activity" element={<AdminSecurityActivity />} />
                        <Route path="branding" element={<AdminBranding />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Public & Regular User Routes */}
                    <Route path="*" element={
                        <>
                            <Navbar />
                            <main className="flex-1">
                                <AnimatePresence mode="wait">
                                    <Routes location={background || location} key={(background || location).pathname}>
                                        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
                                        <Route path="/property/:id" element={<PageTransition><PropertyDetails /></PageTransition>} />
                                        <Route path="/featured" element={<PageTransition><FeaturedProperties /></PageTransition>} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                                    <Route path="/about" element={<AboutUs />} />
                                    <Route path="/contact" element={<ContactUs />} />
                                    <Route path="/pricing" element={<SubscriptionPayment />} />
                                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                    <Route path="/terms-of-service" element={<TermsOfService />} />
                                    <Route path="/account-disabled" element={<AccountDisabled />} />

                                    <Route path="/activate" element={<ActivateAccount />} />
                                    <Route path="/payment-upload" element={<Navigate to="/pricing" />} />
                                    <Route 
                                        path="/dashboard" 
                                        element={
                                            <ProtectedRoute>
                                                <Dashboard />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/portfolio" 
                                        element={
                                            <SellerRoute>
                                                <Portfolio />
                                            </SellerRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/support-history" 
                                        element={
                                            <ProtectedRoute>
                                                <SupportHistory />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/notifications/admin-messages" 
                                        element={
                                            <ProtectedRoute>
                                                <UserAdminMessages />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/security-activity" 
                                        element={
                                            <ProtectedRoute>
                                                <LoginHistory />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/profile" 
                                        element={
                                            <ProtectedRoute>
                                                <Profile />
                                            </ProtectedRoute>
                                        } 
                                    />
                                        <Route path="*" element={<Navigate to="/" replace />} />
                                    </Routes>
                                </AnimatePresence>
                            </main>
                            <Footer />
                        </>
                    } />
                </Routes>

                {/* Modal Routes */}
                <AnimatePresence>
                    {background && (
                        <Routes location={location} key={location.pathname}>
                            <Route path="/property/:id" element={<PropertyDetails isModal={true} />} />
                        </Routes>
                    )}
                </AnimatePresence>
            </Suspense>
        </div>
    );
};

const App = () => {
    return (
        <HelmetProvider>
            <BrandingProvider>
                <AuthProvider>
                    <ScrollToTop />
                    <AppContent />
                </AuthProvider>
            </BrandingProvider>
        </HelmetProvider>
    );
};

export default App;
